import { useShallow } from 'zustand/shallow'

import type { Item } from '@/stores/CartStore'
import useCartStore from '@/stores/CartStore'
import useDialogsStore from '@/stores/DialogsStore'

// * Domain constants
export const TEMPERATURE_SENSOR_PARTS = [
  'R35825', // ReefSense Temperature probe
  'R35821', // ReefSense pH & Temp Probe
  'R35820', // ReefSense Salinity & Temp Probe
  'R35823', // ATO module with integrated digital temperature sensor
] as const

export const REEFCONTROL_POWER = 'R35817'
export const REEFCONTROL_POWER_DELUXE = 'R35818'

// * Domain types
enum ConflictType {
  DUPLICATE_TEMPERATURE_SENSOR = 'duplicateTemperatureSensor',
  POWER_CENTER_UPGRADE = 'powerCenterUpgrade',
}

type ConflictDetectionResult = {
  hasConflict: boolean
  conflictType?: ConflictType
  conflictingItem?: Item
}

// ? Helpers
const getActiveCartItemNumbers = (items: Item[], hubId: string): string[] =>
  items
    .filter(cartItem => cartItem.hubId === hubId && cartItem.amount > 0)
    .map(cartItem => cartItem.rNumber)

const itemHasTemperatureSensor = (rNumber: string): boolean =>
  TEMPERATURE_SENSOR_PARTS.includes(rNumber as typeof TEMPERATURE_SENSOR_PARTS[number])

const cartHasTemperatureSensor = (cartItemNumbers: string[]): boolean =>
  TEMPERATURE_SENSOR_PARTS.some(part => cartItemNumbers.includes(part))

const cartHasBasicPowerCenter = (cartItemNumbers: string[]): boolean =>
  cartItemNumbers.includes(REEFCONTROL_POWER)

const itemIsDeluxePowerCenter = (rNumber: string): boolean =>
  rNumber === REEFCONTROL_POWER_DELUXE

// ? Business rule functions
const hasDuplicateTemperatureSensor = (
  item: Item,
  cartItemNumbers: string[],
): boolean =>
  itemHasTemperatureSensor(item.rNumber) && cartHasTemperatureSensor(cartItemNumbers)

const isPowerCenterUpgrade = (
  item: Item,
  cartItemNumbers: string[],
): boolean =>
  cartHasBasicPowerCenter(cartItemNumbers) && itemIsDeluxePowerCenter(item.rNumber)

const detectConflict = (
  item: Item,
  cartItemNumbers: string[],
  getByRNumber: (rNumber: string) => Item | undefined,
): ConflictDetectionResult => {
  if (hasDuplicateTemperatureSensor(item, cartItemNumbers)) {
    const existingSensorRNumber = cartItemNumbers.find(rNumber =>
      TEMPERATURE_SENSOR_PARTS.includes(rNumber as typeof TEMPERATURE_SENSOR_PARTS[number]),
    )

    if (existingSensorRNumber) {
      const conflictingItem = getByRNumber(existingSensorRNumber)
      if (conflictingItem) {
        return {
          hasConflict: true,
          conflictType: ConflictType.DUPLICATE_TEMPERATURE_SENSOR,
          conflictingItem,
        }
      }
    }
  }

  if (isPowerCenterUpgrade(item, cartItemNumbers)) {
    return {
      hasConflict: true,
      conflictType: ConflictType.POWER_CENTER_UPGRADE,
    }
  }

  return { hasConflict: false }
}

// Main hook
const useItemConflictWarning = (item: Item) => {
  const cartItems = useCartStore(useShallow(state => state.items))
  const getByRNumber = useCartStore(state => state.getByRNumber)

  const { setWarningInfo, toggle } = useDialogsStore(useShallow(state => ({
    toggle: state.toggle,
    setWarningInfo: state.setWarningInfo,
  })))

  const activeCartItemNumbers = getActiveCartItemNumbers(cartItems, item.hubId)

  const showConflictWarning = (conflict: ConflictDetectionResult): void => {
    switch (conflict.conflictType) {
      case ConflictType.DUPLICATE_TEMPERATURE_SENSOR:
        if (conflict.conflictingItem) {
          setWarningInfo('temperatureSensor', item, conflict.conflictingItem)
        }
        break
      case ConflictType.POWER_CENTER_UPGRADE:
        setWarningInfo('powerCenter', item)
        break
    }
    toggle('warning')
  }

  const detectAndShowConflictWarning = (): boolean => {
    const conflict = detectConflict(item, activeCartItemNumbers, getByRNumber)

    if (conflict.hasConflict) {
      showConflictWarning(conflict)
      return true
    }

    return false
  }

  return {
    checkWarningDialog: detectAndShowConflictWarning,
    // ? Only for testing
    hasConflict: () => detectConflict(item, activeCartItemNumbers, getByRNumber).hasConflict,
  }
}

export default useItemConflictWarning
